import { inject } from "@angular/core";
import { IdArray, IdObject } from "@app/_classes/cosmos-entity";
import { logger } from "@app/_helpers/logger";
import {
	Project,
	ProjectPermissionRole,
	UserSettings,
	UserSettingsQuery,
	Workspace,
	WorkspacePermissionRole,
} from "@timeghost/web-api";
import { map } from "rxjs";
import { NoIndex } from "./types";
import { userProjectTaskPerm } from "./userProjectFind";
import { DEFAULT_PERMISSION_GROUPS, fromRxValue, hasPermission, hasUserInProject } from "./utils";

export function isAdmin(user: UserSettings) {
	return hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, user);
}
export function isSupervisor(user: UserSettings) {
	return !!user.workspace?.users?.find((d) => d["supervisors"]?.find((s: IdObject) => s.id === user.id));
}

export function isSupervisorOfUser(user: UserSettings, subordinateId: string) {
	return !!user.workspace?.users?.find(
		(d) => d.id === subordinateId && d["supervisors"]?.find((s: IdObject) => s.id === user.id)
	);
}
export function isSubordinate(
	user: UserSettings,
	subordinateId: string
): { supervisorId: string; subordinateId: string } | null {
	return (
		(!!user.workspace?.users?.find(
			(d) => subordinateId === d.id && d["supervisors"]?.find((s: IdObject) => s.id === user.id)
		) && { subordinateId, supervisorId: user.id }) ||
		null
	);
}

export function hasUserRoleManager(user: UserSettings) {
	return user.workspace?.users?.find((d) => d.id == user.id)?.role === "manager";
}
export function hasUserRoleControlling(user: UserSettings) {
	return user.workspace?.users?.find((d) => d.id == user.id)?.role === "controlling";
}
export function checkWorkspaceUserRole(user: UserSettings, role: WorkspacePermissionRole) {
	if (role === "admin") return isAdmin(user);
	if (role === "supervisor") return isSupervisor(user);
	if (!user?.workspace) return false;
	return checkUserRole(user.workspace.users.find((d) => d.id === user.id) as any, role);
}
/**
 * Checks user or project entity role - if role is not provided, returns false
 * @param entity
 * @param role
 * @returns
 */
export function checkProjectEntityRole<TEntity extends { id: string; role?: ProjectPermissionRole }>(
	user: TEntity | null | undefined,
	role: ProjectPermissionRole
) {
	if (!user) return false;
	if (!user.role) return false;
	return user.role === role;
}
/**
 * Checks user or project entity role - since its the same object structure, we can use the same function
 * @param user
 * @param role
 * @returns
 */
export function checkUserRole<
	TPermissionRole extends WorkspacePermissionRole | ProjectPermissionRole,
	TUser extends { id: string; role?: TPermissionRole } | null | undefined,
>(user: TUser, role: TPermissionRole) {
	if (!user) return false;
	if (!user.role) return false;
	return user.role === role;
}
/**
 * create a function to test if a provided userId is supervised from current user, checks users and groups for subordinates
 */
export function isProjectManagerOfUser(user: UserSettings) {
	return (userId: string) =>
		!!user.workspace?.projectPermissions?.find((d) => {
			if (!d.users?.find((u) => u.id === user.id && checkProjectEntityRole(u, "manager") && !u.removed))
				return false;
			return (
				d.users.findIndex((u) => u.id === userId) !== -1 ||
				d.groups?.find(
					(d) =>
						!!user.workspace.groups?.find(
							(g) =>
								g.id === d.id &&
								!!checkProjectEntityRole(
									g.users?.find((u) => u.id === userId),
									"manager"
								)
						)
				)
			);
		});
}
export function useIsUserOnSameProject(user: UserSettings, searchPrivate: boolean = false) {
	const userProjects = user.workspace.projectPermissions.filter(
		(p) => (searchPrivate || !p.private) && p.users?.find((pu) => pu.id === user.id && !pu.removed)
	);
	return (userId: string) =>
		!!userProjects?.find((d) => {
			return (
				d.users?.find((u) => u.id === userId && !u.removed) ||
				d.groups?.find(
					(d) =>
						!!user.workspace.groups?.find((g) => g.id === d.id && !!g.users?.find((u) => u.id === userId))
				)
			);
		});
}
export function useUserInProject(user: UserSettings, searchPrivate: boolean = false) {
	const userProjects = user.workspace.projectPermissions.filter((p) => searchPrivate || !p.private);
	return (userId: string) =>
		!!userProjects?.find((d) => {
			return (
				d.users?.find((u) => u.id === userId && !u.removed) ||
				d.groups?.find(
					(d) =>
						!!user.workspace.groups?.find((g) => g.id === d.id && !!g.users?.find((u) => u.id === userId))
				)
			);
		});
}
export function createTimePermissionFilter(user: UserSettings, type: "project" | "workinghours" = "workinghours") {
	const userIsAdmin = isAdmin(user);
	const userCanSupervise = isSupervisor(user);
	const userProjectManagerSupervisor = isProjectManagerOfUser(user);
	return (times: { user: { id: string } }[]) =>
		times.filter((d) => {
			const subordinate = isSubordinate(user, d.user.id);
			if (type === "workinghours")
				return d.user.id === user.id || userIsAdmin || (userCanSupervise && subordinate);
			else return d.user.id === user.id || userIsAdmin || userProjectManagerSupervisor(d.user.id);
		});
}
export function createUserPermissionFilter(user: UserSettings, type: "project" | "workinghours" = "workinghours") {
	const userIsAdmin = isAdmin(user);
	const userIsManager = hasUserRoleManager(user);
	const userIsControlling = hasUserRoleControlling(user);
	const userProjectManagerSupervisor = isProjectManagerOfUser(user);
	const canViewPublicTimes = hasPermissionByKey(user, "groupsCanSeePublicTimes");
	const canViewPrivateTimes = hasPermissionByKey(user, "groupsCanSeePrivateTimes");
	const comegoEnabled = user.workspace.settings?.workinghoursEnabled ?? true;
	return (users: { id: string }[]) =>
		users.filter((d) => {
			const subordinate = isSupervisorOfUser(user, d.id);
			if (type === "workinghours")
				return (
					d.id === user.id ||
					userIsAdmin ||
					userIsManager ||
					subordinate ||
					(!comegoEnabled && userIsControlling)
				);
			else
				return (
					d.id === user.id ||
					userIsAdmin ||
					userIsManager ||
					userIsControlling ||
					userProjectManagerSupervisor(d.id) ||
					canViewPublicTimes ||
					canViewPrivateTimes
				);
		});
}
type PMS = Workspace["permissionSettings"] & {
	groupsCanComegoViewTimes: IdArray;
	groupsCanComegoExportTimes: IdArray;
	groupsCanComegoCreateTimes: IdArray;
	groupsCanCreateAbsences: IdArray;
	groupsCanComegoUpdateTimes: IdArray;
	groupsCanContingentViewSelf: IdArray;
	groupsCanContingentViewOther: IdArray;
	groupsCanManageAllProjects: IdArray;
	groupsCanSeePrivateTimes: IdArray;
	groupsCanSeeDashboardInNavigation: IdArray;
	groupsCanSeeTeamActivityWorkinghours: IdArray;
	groupsCanSeeTeamActivity: IdArray;
	groupsCanExportWorkinghours: IdArray;
	groupsCanExportProjecthours: IdArray;
	groupsCanExportContingent: IdArray;
};
type PMSKnown = keyof NoIndex<PMS>;
export function hasPermissionByKey(user: UserSettings, key: PMSKnown) {
	if (isAdmin(user)) return true;
	return !!user.workspace?.permissionSettings?.[key]?.find((p: { id: string }) => hasPermission(p.id, user));
}
export function hasCreateTaskPermission(user: UserSettings, project: Project) {
	if (!project) return false;
	return userProjectTaskPerm(user, project);
}
export function hasCreateProjectPermission(user: UserSettings) {
	return hasPermissionByKey(user, "groupsCanManageProjects");
}
export function isProjectUser(uid: string, workspace: Workspace, project: Project) {
	return hasUserInProject({ id: uid, workspace } as any, project);
}
export function isProjectManager(uid: string, workspace: Workspace, project?: Project) {
	return !!workspace?.projectPermissions?.find(
		(x) =>
			(!project || x.projectId === project.id) &&
			(x.users?.find((u) => u.id === uid && checkProjectEntityRole(u, "manager") && !u.removed) ||
				x.groups?.find((g) => {
					if (g.role !== "manager") return false;
					const group = workspace.groups?.find((wg) => wg.id === g.id);
					if (!group) return false;
					return group.users?.find((gu) => gu.id === uid);
				}))
	);
}
export function canAccessProjectSummaryExport(user: UserSettings) {
	return isAdmin(user) || hasUserRoleControlling(user) || hasUserRoleManager(user);
}

export function useAdmin() {
	const user = inject(UserSettingsQuery);
	return fromRxValue(() => user.select().pipe(map((u) => isAdmin(u))), isAdmin(user.getValue()));
}

export function canViewDashboard(user: UserSettings) {
	// workspace Settings
	const workinghoursEnabled = (user.workspace.settings?.workinghoursEnabled as boolean) ?? false;
	const projecthoursEnabled = (user.workspace.settings?.projecthoursEnabled as boolean) ?? false;
	const timecontingentEnabled = !!user.workspace["timecontingent"]?.enabled && workinghoursEnabled;
	// users Roles
	const userIsAdmin = isAdmin(user);
	const userIsSupervisor = isSupervisor(user);
	const userIsProjectManager = isProjectManager(user.id, user.workspace);
	// worksapce permissions
	const canSeeDashboardNavigation = hasPermissionByKey(user, "groupsCanSeeDashboardInNavigation");

	const canSeePublicTimes = hasPermissionByKey(user, "groupsCanSeePublicTimes");
	const canSeePrivateTimes = hasPermissionByKey(user, "groupsCanSeePrivateTimes");
	const canSeeOwnContingent = hasPermissionByKey(user, "groupsCanContingentViewSelf");
	const canSeeSubordinatesContingent =
		!!user.workspace["timecontingent"]?.superviseCanViewSubordinates && userIsSupervisor;
	const canEditWorkinghours = hasPermissionByKey(user, "groupsCanComegoViewTimes");
	const canViewWorkinghours = hasPermissionByKey(user, "groupsCanComegoExportTimes");

	// permission on dashboard tab
	const projectPermission =
		projecthoursEnabled && (userIsAdmin || canSeePublicTimes || canSeePrivateTimes || canSeeDashboardNavigation);
	const workingHoursPermission = workinghoursEnabled
		? userIsAdmin || canEditWorkinghours || canViewWorkinghours
		: projectPermission;
	const timecontingentPermission = timecontingentEnabled && (canSeeOwnContingent || canSeeSubordinatesContingent);
	const canAccessDashboard =
		canSeeDashboardNavigation || workingHoursPermission || timecontingentPermission || projectPermission;

	if (!canAccessDashboard) return false;

	let dashboardUrl: string | null = null;
	if (!workinghoursEnabled) {
		if (projectPermission) {
			dashboardUrl = "/dashboard";
		}
	} else {
		if (workingHoursPermission) {
			dashboardUrl = "/dashboard/schedules";
		} else if (projectPermission) {
			dashboardUrl = "/dashboard";
		} else if (timecontingentPermission) {
			dashboardUrl = "/dashboard/contingent";
		}
	}
	return {
		userIsAdmin,
		userIsSupervisor,
		projecthoursEnabled,
		projectPermission,
		workingHoursPermission,
		timecontingentPermission,
		dashboardUrl,
	};
}
export function canViewUser(user: UserSettings): { id: string; name: string }[] {
	// todo
	return [];
}
export function isUserInGroup(uid: string, groupId: string, userSettings: UserSettings) {
	const group = userSettings.workspace.groups.find((g) => g.id === groupId);
	if (!group) return false;
	const user = group.users.find((u) => u.id === uid);
	return !!user;
}

export function usePermission() {
	const user = inject(UserSettingsQuery);

	return fromRxValue(
		user.select().pipe(
			map((user) => {
				if (!user) return null;
				const projecthoursEnabled = user.workspace.settings?.projecthoursEnabled ?? false;
				const isPM = isProjectManager(user.id, user.workspace);
				const manageProjects = hasPermissionByKey(user, "groupsCanManageProjects");
				const manageAllProjects = hasPermissionByKey(user, "groupsCanManageAllProjects");
				const manageClients = hasPermissionByKey(user, "groupsCanManageClients");
				const manageTags = hasPermissionByKey(user, "groupsCanManageTags");
				const workinghoursEnabled = user.workspace.settings?.workinghoursEnabled ?? false;
				const seePublicTimes = hasPermissionByKey(user, "groupsCanSeePublicTimes");
				const seeBillableRates = hasPermissionByKey(user, "groupsCanSeeBillableRates");
				const seePrivateTimes = hasPermissionByKey(user, "groupsCanSeePrivateTimes");
				const seeTeamActivity = hasPermissionByKey(user, "groupsCanSeeTeamActivity");
				const permissions = {
					groupsCanManageProjects: manageProjects,
					groupsCanManageAllProjects: manageAllProjects,
					groupsCanManageClients: manageClients,
					groupsCanManageTags: manageTags,
					isProjectManager: isPM,
					seePublicTimes,
					seeBillableRates,
					seeTeamActivity,
					projecthours: projecthoursEnabled
						? {
								times: seePublicTimes || seePrivateTimes,
							}
						: undefined,
					workinghours: workinghoursEnabled
						? {
								times:
									hasPermissionByKey(user, "groupsCanComegoCreateTimes") ||
									hasPermissionByKey(user, "groupsCanComegoUpdateTimes") ||
									hasPermissionByKey(user, "groupsCanComegoViewTimes") ||
									hasPermissionByKey(user, "groupsCanComegoExportTimes"),
								createTime: hasPermissionByKey(user, "groupsCanComegoCreateTimes"),
								updateTime: hasPermissionByKey(user, "groupsCanComegoUpdateTimes"),
								viewTime: hasPermissionByKey(user, "groupsCanComegoViewTimes"),
								exportTime: hasPermissionByKey(user, "groupsCanComegoExportTimes"),
								contingent:
									hasPermissionByKey(user, "groupsCanContingentViewSelf") ||
									hasPermissionByKey(user, "groupsCanContingentViewOther"),
								contingentSupervise:
									hasPermissionByKey(user, "groupsCanContingentViewOther") &&
									user.workspace["timecontingent"]?.["superviseCanViewSubordinates"],
								contingentSuperviseSubordinates:
									hasPermissionByKey(user, "groupsCanContingentViewOther") &&
									user.workspace["timecontingent"]?.["superviseCanViewSubordinates"],
							}
						: undefined,
					exportPermissions: {
						exportProjectHours: projecthoursEnabled
							? hasPermissionByKey(user, "groupsCanExportProjecthours")
							: undefined,
						exportWorkinghours: workinghoursEnabled
							? hasPermissionByKey(user, "groupsCanExportWorkinghours")
							: projecthoursEnabled
								? hasPermissionByKey(user, "groupsCanExportProjecthours")
								: undefined,
						exportContingent: workinghoursEnabled
							? hasPermissionByKey(user, "groupsCanExportContingent")
							: undefined,
					},
				};
				logger.debug("permissions", permissions);
				return permissions;
			})
		)
	);
}

const projectPermArray = ["member", "manager"] as const;
export const resolveProjectPermissionRole = (role: ProjectPermissionRole) => {
	if (typeof role === "number") role = projectPermArray[role] as ProjectPermissionRole;
	return role;
};
const workspacePermArray = ["member", "admin"] as const;
export const resolveWorkspacePermissionRole = (role: WorkspacePermissionRole) => {
	if (typeof role === "number") role = workspacePermArray[role] as WorkspacePermissionRole;
	return role;
};
